import { useCallback, useMemo, useRef, useState } from "react";
import { captureException } from "@sentry/nextjs";
import { PropertyDataV2 } from "commonModule/services/common";
import { CountApiListOfAllProperties } from "commonModule/services/search";
import BuildingUnitIcon from "components/icons-svg/BuildingUnitIcon";
import useCurrency from "hooks/swr/useCurrency";
import useFavorites from "hooks/swr/useFavorites";
import { useIsAgent } from "hooks/useIsAgent";
import { apiClientCommon } from "services/apiClientCommon";
import { selectIsUserLoggedIn } from "stores/auth/authSlice";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectSearchAddressResult } from "stores/save-search/saveSearchSlice";
import { selectSearchQuery, selectShouldDisplayTotalPrice } from "stores/search/searchSlice";
import { markUI, selectOpenedPropertyCardInMapBox } from "stores/ui/uiSlice";
import { getCookie } from "utils/cookies";
import { getAddressComponents } from "utils/helper";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { useOutsideClick } from "utils/hooks/useOutsideClick";
import InfoPopup from "../InfoPopup";
const DotMarker = ({
  property,
  getPropertyData,
  hasMultiple
}: {
  property: CountApiListOfAllProperties;
  getPropertyData: (data: PropertyDataV2) => void;
  hasMultiple: boolean;
}) => {
  const search = useAppSelector(selectSearchQuery);
  const isAgent = useIsAgent();
  const searchAddress = useAppSelector(selectSearchAddressResult);
  const isSearchAddress = getAddressComponents(searchAddress?.address_components || []);
  const isLoggedIn = useAppSelector(selectIsUserLoggedIn);
  const dispatch = useAppDispatch();
  const dotPopupRef = useRef<HTMLDivElement | null>(null);
  const [isDesktop] = useMediaQuery("(min-width: 1280px)");
  const [isCloseOnOutsideEnabled, setIsCloseOnOutsideEnabled] = useState(true);
  const {
    rate,
    formatter
  } = useCurrency();
  const isgoogle = getCookie("isGoogle") === "true";
  const [propertiesDotData, setPropertiesDotData] = useState<PropertyDataV2 | null>(null); // State to store API response

  // function to close the info popup when user click on close button
  const onClose = useCallback(() => {
    dispatch(markUI({
      openedPropertyCardInMapBox: null
    }));
  }, [dispatch]);
  const openedPropertyCardInMapBox = useAppSelector(selectOpenedPropertyCardInMapBox);
  const shouldDisplayTotalPrice = useAppSelector(selectShouldDisplayTotalPrice) && !!propertiesDotData?.checkInDate && !!propertiesDotData?.checkOutDate || !!search?.startDate && !!search.endDate;
  const favPropertyIds = useFavorites();
  const isFavourite = useMemo(() => {
    return !!favPropertyIds.find(id => id === property.id);
  }, [favPropertyIds, property.id]);
  const normalizeToArray = (data: PropertyDataV2 | PropertyDataV2[]): PropertyDataV2[] => {
    return Array.isArray(data) ? data : [data];
  };
  const fetchDotPropertyCard = () => {
    if (!property?.id) return; // Guard clause to ensure property.id is available

    void apiClientCommon.searchDotPropertyCard({
      params: {
        id: property.id,
        noOfDays: search?.noOfDays || 0,
        startDate: search?.startDate || "",
        numberOfGuest: search?.numberOfGuest || 0,
        petAllowed: search?.petAllowed || false,
        allInPriceToggle: true,
        isgoogle
      }
    }).then(response => {
      if (response?.status === 200) {
        const units = normalizeToArray(response.data.data);
        // when user click on dot button then we need to update the openedPropertyCardInMapBox state
        // so open then info popup
        dispatch(markUI({
          openedPropertyCardInMapBox: units[0]._id
        }));
        setPropertiesDotData(units[0]); // store the API response in state
        getPropertyData(units[0]);

        // mixpanel tracking
        void (async () => {
          const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
          mixpanelV2Client?.CLICK_DOT({
            city: isSearchAddress.city || "NA",
            state: isSearchAddress.state || "NA",
            country: isSearchAddress.country || "NA",
            propertyId: property.id
          });
        })();
      }
      return true;
    }).catch(err => {
      console.error(err);
      captureException(new Error(`Error 'searchDotPropertyCard' ${JSON.stringify(err)}`));
    });
  };
  const isPopupShow = openedPropertyCardInMapBox && propertiesDotData && openedPropertyCardInMapBox === propertiesDotData._id;
  const isMultiUnitProperty = propertiesDotData?.multiUnitGrp && propertiesDotData?.multiUnitCount && propertiesDotData?.multiUnitCount > 1 && hasMultiple;

  // use function to handle outside click
  useOutsideClick({
    ref: dotPopupRef,
    enabled: isDesktop && propertiesDotData !== null && openedPropertyCardInMapBox === propertiesDotData._id && isCloseOnOutsideEnabled,
    handler: event => {
      const target = (event.target as HTMLElement | null);
      if (target?.closest(".map-marker")) return;
      onClose();
    }
  });
  const hasDates = !!search?.startDate && !!search?.endDate;
  const shouldShowPrice = isLoggedIn && hasDates && isAgent;
  return <>
      {isPopupShow && shouldShowPrice ? <button id={`trigger-${property.id}`} className={`map-marker font-sans ${isMultiUnitProperty ? "flex items-center gap-2 whitespace-nowrap" : ""} ${openedPropertyCardInMapBox === property.id ? "border-[#FFDD50] bg-[#FFDD50] text-brand-dark" : "border-[#5DC9D6] bg-secondary500 text-white"}`}>
          {isMultiUnitProperty && <BuildingUnitIcon stroke={openedPropertyCardInMapBox === property.id ? "#26262B" : "white"} />}
          {shouldDisplayTotalPrice ? `${formatter(0, 0).format((propertiesDotData?.totalBeforeTaxes || 0) * rate)}` : `${formatter(0, 0).format(propertiesDotData?.minPrice * rate)}`}
        </button> : <button id={`trigger-${property.id}`} aria-label='property-price-marker' className='property-marker-styles z-1 h-2.5 w-[17px] -translate-x-1/2 -translate-y-1/2 transform rounded-[9px] bg-secondary500 hover:scale-150 hover:bg-[#FFDA1C]' onClick={fetchDotPropertyCard}>
          <span className='sr-only'>View property marker</span>{" "}
          {/* this is for "Buttons do not have descriptive text" */}
        </button>}

      {/* here open the info popup when user click on dot button and when openedPropertyCardInMapBox is equal to propertiesDotData._id*/}
      {isPopupShow && <div ref={dotPopupRef} id={`popoverbody-${propertiesDotData?._id}`} className='absolute z-[99] hidden rounded-lg bg-offWhite font-sans sxm:w-[320px] xl:block'>
          <InfoPopup property={propertiesDotData} isFavourite={isFavourite} onClose={onClose} setIsCloseOnOutsideEnabled={setIsCloseOnOutsideEnabled} pageOrigin='map - clicked dot' hasMultiple={false} />
        </div>}
    </>;
};
export default DotMarker;